import React, { useState, useEffect } from 'react'

import { CookieSettingsType, DEFAULT_COOKIE_SETTINGS } from '../types/cookie'
import { cookieSettingsModalText } from '../constants/cookie-settings-modal-text'

interface CookieSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (settings: CookieSettingsType) => void
  currentSettings?: CookieSettingsType
}

export const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentSettings,
}) => {
  const [settings, setSettings] = useState<CookieSettingsType>(DEFAULT_COOKIE_SETTINGS)

  useEffect(() => {
    if (currentSettings) {
      setSettings(currentSettings)
    }
  }, [currentSettings])

  const handleToggle = (key: keyof CookieSettingsType) => {
    if (key === 'strictlyNecessary') return // Cannot be toggled
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleConfirm = () => {
    onSave(settings)
  }

  const handleAcceptAll = () => {
    const allAccepted: CookieSettingsType = {
      strictlyNecessary: true,
      performance: true,
      functional: true,
      targeting: true,
    }
    setSettings(allAccepted)
    onSave(allAccepted)
  }

  const handleRejectAll = () => {
    const allRejected: CookieSettingsType = {
      strictlyNecessary: true,
      performance: false,
      functional: false,
      targeting: false,
    }
    setSettings(allRejected)
    onSave(allRejected)
  }

  // 쿠키 카테고리 데이터 정의
  const cookieCategories: {
    key: keyof Omit<CookieSettingsType, 'strictlyNecessary'>
    title: string
    description: string
  }[] = [
    {
      key: 'performance',
      title: cookieSettingsModalText.categories.performance.title,
      description: cookieSettingsModalText.categories.performance.description,
    },
    {
      key: 'functional',
      title: cookieSettingsModalText.categories.functional.title,
      description: cookieSettingsModalText.categories.functional.description,
    },
    {
      key: 'targeting',
      title: cookieSettingsModalText.categories.targeting.title,
      description: cookieSettingsModalText.categories.targeting.description,
    },
  ]

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[205] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{cookieSettingsModalText.title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">
              ×
            </button>
          </div>

          <div className="mb-6 text-sm text-gray-600 leading-relaxed">{cookieSettingsModalText.description}</div>

          <div className="space-y-4">
            {/* Strictly Necessary Cookies */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {cookieSettingsModalText.categories.strictlyNecessary.title}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {cookieSettingsModalText.categories.strictlyNecessary.alwaysActive}
                  </span>
                </div>
                <div className="bg-gray-300 rounded-full w-12 h-6 flex items-center justify-end px-1">
                  <div className="bg-gray-500 w-4 h-4 rounded-full"></div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {cookieSettingsModalText.categories.strictlyNecessary.description}
              </p>
            </div>
            {cookieCategories.map(({ key, title, description }) => (
              <div key={key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-gray-900">{title}</h3>
                  <button
                    onClick={() => handleToggle(key)}
                    className={`rounded-full w-12 h-6 flex items-center transition-colors ${
                      settings[key] ? 'bg-orange500 justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="bg-white w-4 h-4 rounded-full mx-1 shadow"></div>
                  </button>
                </div>
                <p className="text-sm text-gray-600">{description}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleConfirm}
              className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              {cookieSettingsModalText.buttons.confirm}
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2 bg-orange500 text-white rounded-md hover:bg-orange600 transition-colors"
            >
              {cookieSettingsModalText.buttons.allowAll}
            </button>
            <button
              onClick={handleRejectAll}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              {cookieSettingsModalText.buttons.rejectAll}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
